import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const formUrl = 'https://readdy.ai/api/form/d9rftt4nlsngrm4lmbn0';
  const [formStatus, setFormStatus] = useState<{
    type: 'idle' | 'success' | 'error';
    message: string;
  }>({ type: 'idle', message: '' });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formUrl) return;

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus({ type: 'success', message: '提交成功！我们将尽快与您联系。' });
      return;
    }
    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch (_) {
        parsed = {};
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', message: '提交成功！我们将尽快与您联系。' });
        form.reset();
      } else {
        const errorMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (String(errorMsg).toLowerCase().includes('spam')) {
          setFormStatus({ type: 'error', message: '提交失败，请稍后重试' });
        } else {
          setFormStatus({ type: 'error', message: errorMsg });
        }
      }
    } catch (_) {
      setFormStatus({ type: 'error', message: '网络错误，请稍后重试' });
    }
  };

  const teamMembers = [
    {
      name: '资深体育编辑团队',
      role: '内容创作',
      description: '由拥有10年以上体育媒体经验的编辑组成',
      icon: 'ri-team-line',
    },
    {
      name: '数据分析专家',
      role: '竞技分析',
      description: '运用先进数据分析工具提供专业赛事洞察',
      icon: 'ri-bar-chart-line',
    },
    {
      name: '技术支持团队',
      role: '平台维护',
      description: '保障平台7×24小时稳定运行与数据实时更新',
      icon: 'ri-code-s-slash-line',
    },
    {
      name: '客户服务团队',
      role: '用户支持',
      description: '专业客服全天候为用户提供优质服务体验',
      icon: 'ri-customer-service-2-line',
    },
  ];

  const milestones = [
    { year: '2018', event: '开云体育平台正式上线，开启体育资讯服务' },
    { year: '2019', event: '用户数突破50万，覆盖国内外主流体育赛事' },
    { year: '2020', event: '推出实时比分与数据分析功能，平台全面升级' },
    { year: '2021', event: '用户数突破200万，成为领先的体育资讯平台' },
    { year: '2022', event: '新增电竞赛事板块，拓展数字体育领域' },
    { year: '2023', event: '平台用户突破500万，服务覆盖全球150+国家' },
    { year: '2024', event: '持续优化产品体验，推出个性化赛事推荐功能' },
  ];

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[200px] md:h-[280px] flex items-end overflow-hidden">
          <div className="absolute inset-0 bg-foreground-900">
            <div className="absolute inset-0 opacity-15 bg-primary-500"></div>
            <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-background-50 to-transparent"></div>
          </div>
          <div className="relative z-10 w-full pb-6 md:pb-8">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb items={[{ label: '关于我们' }]} />
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                关于开云体育
              </h1>
              <div className="w-16 h-1 bg-primary-500 mx-auto mb-5 rounded-full"></div>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                开云体育成立于2018年，是专业的体育赛事资讯平台。我们汇聚全球顶级联赛信息，为体育爱好者提供实时
                赛事资讯、赛程直播与竞技数据分析服务。开云体育始终秉持"专业、全面、及时"的服务理念，致力于为
                用户打造一站式的体育娱乐体验。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-14">
              {teamMembers.map((member, idx) => (
                <div
                  key={idx}
                  className="text-center p-5 rounded-xl bg-background-100 transition-all duration-300 hover:scale-105"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${member.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1 whitespace-nowrap">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-600 font-medium mb-1">{member.role}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2"></div>
              <div className="space-y-8">
                {milestones.map((m, idx) => (
                  <div
                    key={idx}
                    className={`relative flex items-center gap-6 md:gap-0 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:block flex-1"></div>
                    <div className="relative z-10 w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold shadow-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1 bg-background-50 rounded-xl p-4 md:p-5 transition-all duration-300 hover:scale-[1.02]">
                      <span className="text-xs font-heading font-bold text-primary-600">{m.year}</span>
                      <p className="text-sm text-foreground-700 mt-1 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              <div>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">service@kaiyunsports.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-phone-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">客服热线</h3>
                      <p className="text-sm text-foreground-600">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-time-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">服务时间</h3>
                      <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                  <input type="text" name="phone_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200 resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-lg transition-all duration-300 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                  >
                    提交留言
                  </button>
                  {formStatus.type !== 'idle' && (
                    <p
                      className={`text-sm text-center ${
                        formStatus.type === 'success' ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {formStatus.message}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-12 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              加入开云体育
            </h2>
            <p className="text-foreground-600 text-sm mb-6">立即体验全球顶级体育赛事，感受竞技魅力</p>
            <a
              href="/category/football"
              className="inline-flex px-8 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full transition-all duration-300 hover:bg-primary-600 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              立即开始
            </a>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于开云体育',
            description: '开云体育成立于2018年，是专业的体育赛事资讯平台。我们汇聚全球顶级联赛信息，为体育爱好者提供实时赛事资讯、赛程直播与竞技数据分析服务。',
            url: 'https://www.kaiyunsports.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '开云体育',
              url: 'https://www.kaiyunsports.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyunsports.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
          }),
        }}
      />
    </>
  );
}